<template>
  <div
    class="thanks-page booking-detail user-account-layout"
    :class="{ desktop: $isDesktop }"
  >
    <section class="intro">
      <p><translate tag="em"> Thank you for your booking. </translate></p>
      <translate tag="p" class="success" :tt="{ created: thanks.created }">
        Your booking was successful on {created, puDate, full} at {created,
        time, short}
      </translate>

      <p>
        <template v-if="thanks.textMessage">
          <translate :tt="{ email: thanks.email, telephone: thanks.telephone }">
            We have sent a confirmation email containing the information below
            to {email} and a text message to {telephone}.
          </translate>
        </template>
        <template v-else>
          <translate :tt="{ email: thanks.email }">
            We have sent a confirmation email containing the information below
            to {email}.
          </translate>
        </template>
      </p>

      <translate tag="p">
        If you don't receive an email right away, please check your spam folder
        and mark 'not spam'.
      </translate>

      <p>
        <pu-link v-bind="bookAgainLinkProps">
          <translate>Make another booking like this</translate>
        </pu-link>
      </p>
    </section>

    <section class="affiliates">
      <how-did-you-hear-about-us :signature="thanks.signature" />
      <translate tag="p">
        What did you think of our booking service?
      </translate>
      <div class="affiliates-row">
        <a
          href="https://www.trustpilot.com/evaluate/www.pitchup.com"
          target="_blank"
          class="affiliate-link trustpilot"
        >
          <img src="@/assets/images/trustpilot-logo.svg" />
          <translate tag="p">Review us on Trustpilot</translate>
        </a>

        <a
          href="https://search.google.com/local/writereview?placeid=ChIJ85wkiw4PdkgRhcMnh4ldCYg"
          target="_blank"
          class="affiliate-link"
        >
          <img src="@/assets/images/google-maps-icon.svg" alt="" />
          <translate tag="p">Review us on Google Maps</translate>
        </a>
      </div>
    </section>

    <base-checkbox
      v-if="thanks.newsletterSubscribed !== undefined"
      :slide-toggle="true"
      :model-value="thanks.newsletterSubscribed === true"
      @update:model-value="updateNewsletter"
      class="email-newsletter-toggle"
    >
      <translate>Subscribe to our recommendations and offers emails</translate>
    </base-checkbox>

    <div class="section-header">
      <translate>You have booked...</translate>
      <translate
        v-if="thanks.prettyId"
        tag="p"
        class="booking-id"
        :tt="{ prettyId: thanks.prettyId }"
      >
        Booking ID: {prettyId}
      </translate>
    </div>

    <section class="campsite-card card">
      <campsite-summary
        class="campsite-summary"
        :name="campsite.name"
        :address="campsite.fullAddress2"
        :photo-url="
          thanks.pitchType.primaryPhoto
            ? `/_cfi/cdn-cgi/image/format=auto,fit=cover,quality=30,w=200,h=200${thanks.pitchType.primaryPhoto.url.masterImage}`
            : undefined
        "
      >
        <pu-rating-bubble
          v-if="ratingObject"
          :rating="ratingObject.rate"
          :rate-count="ratingObject.votes"
        />
        <div class="summary-footer">
          <booking-time-until-holiday
            v-if="thanks.arrive"
            :date="thanks.arrive"
          />
          <pu-campsite-link
            :hierarchy-path="campsite.path"
            :slug="campsite.slug"
            target="_blank"
            class="account-action sml view-site-btn"
            ><translate>View site</translate>
          </pu-campsite-link>
        </div>
      </campsite-summary>
    </section>

    <booking-reservation-details
      :pitch-type="thanks.pitchType"
      :booking="thanks"
      :is-booking-paid="isBookingPaid"
      :is-past-booking="isPastBooking"
      :arrival-times-note="thanks.pitchType.campsite.arrivalTime"
    />

    <booking-notices
      :warnings="thanks.pitchType.campsite.warnings"
      :notices="thanks.pitchType.campsite.notices"
      :notices-needs-translation="
        thanks.pitchType.campsite.noticesNeedsTranslation
      "
      :campsite-notices="thanks.pitchType.campsite.campsiteNotices"
      :restrictions="thanks.pitchType?.restrictions"
      :bring-bed-linen-html="thanks.pitchType?.bringBedLinenHtml"
    />

    <section class="booking-payment" id="payment">
      <div class="payment-header">
        <h3 class="section-heading">
          <img src="@/assets/images/creditcard.svg" width="20" height="21" />
          <translate>Payment</translate>
        </h3>
      </div>

      <table class="costs-table" v-if="thanks.accommodationCost">
        <tbody>
          <tr>
            <translate tag="td"> Accommodation cost </translate>
            <td>
              <pu-price
                :price="getMoney(thanks.accommodationCost)"
                :display-original-price="true"
              />
            </td>
          </tr>
        </tbody>
      </table>

      <template v-if="thanks.bookingExtras && thanks.bookingExtras.length">
        <booking-extras :extras="thanks.bookingExtras" />
      </template>

      <template v-if="showTaxesSection">
        <translate tag="p" class="section-title"> Taxes </translate>

        <table class="costs-table">
          <tbody>
            <tr v-for="(tax, index) of bookingTaxes" :key="index">
              <td>
                <translate v-if="tax.inclusive" :tt="{ taxType: tax.taxType }">
                  Includes {taxType}
                </translate>
                <span v-else>{{ tax.taxType }}</span>
                <translate class="rate" :tt="{ taxRate: tax.rate }">
                  {taxRate, puNumber, style:percent minimumFractionDigits:3}
                </translate>
              </td>
              <td>
                <pu-price
                  :price="getMoney(tax.amount)"
                  :display-original-price="true"
                />
              </td>
            </tr>

            <tr
              v-for="(tax, index) of thanks.bookingarrivaltaxSet"
              :key="index"
            >
              <td>
                <template v-if="tax.inclusive">
                  <translate :tt="{ taxType: tax.taxType }">
                    Includes {taxType}
                  </translate>
                </template>
                <template v-else>
                  <translate :tt="{ taxType: tax.taxType }">
                    Excludes {taxType} tax (paid on arrival)
                  </translate>
                </template>

                <pu-popover v-if="thanks.campsite_TaxTermsHtml">
                  <template #content>
                    <div
                      v-if="thanks.campsite_TaxTermsHtml"
                      v-thtml="thanks.campsite_TaxTermsHtml"
                      class="prerendered tax-terms-html"
                    />
                  </template>
                </pu-popover>
              </td>
              <td>
                <pu-price
                  :price="getMoney(tax.amount)"
                  :display-original-price="true"
                />
              </td>
            </tr>
          </tbody>
        </table>
      </template>

      <table
        class="costs-table totals"
        :class="{
          'with-balance': thanks.remainder.amountMinor > 0 && !isBookingPaid,
        }"
      >
        <tbody>
          <tr>
            <td class="total"><translate>Total stay</translate></td>
            <td>
              <pu-price
                :price="getMoney(thanks.price)"
                :display-original-price="true"
              />
            </td>
          </tr>

          <tr v-if="!thanks.stripecampsitepayment">
            <td class="deposit">
              <i18n-t keypath="Pitchup.com has billed you {price}">
                <template #price>
                  <pu-price
                    :price="getMoney(thanks.deposit)"
                    :display-original-price="true"
                  />
                </template>
              </i18n-t>
            </td>
            <td>
              <pu-price
                :price="getMoney(thanks.deposit)"
                :display-original-price="true"
              />
            </td>
          </tr>

          <tr v-else-if="thanks.stripecampsitepayment.status == 'PENDING'">
            <td class="deposit">
              <i18n-t keypath="Pitchup.com has billed you {price}">
                <template #price>
                  <pu-price
                    :price="getMoney(thanks.deposit)"
                    :display-original-price="true"
                  />
                </template>
              </i18n-t>
            </td>
            <td>
              <pu-price
                :price="getMoney(thanks.deposit)"
                :display-original-price="true"
              />
            </td>
          </tr>

          <tr v-if="thanks.remainder.amountMinor > 0" class="balance">
            <td>
              <translate>Balance</translate>
              <template v-if="thanks.stripecampsitepayment">
                <span class="balance-details">
                  <template v-if="isBookingPaid">
                    <translate tag="strong">Already paid</translate>
                  </template>
                  <template v-else>
                    <translate>Due:</translate>
                    <strong
                      ><pu-date :date="thanks.balanceDueDate" options="short"
                    /></strong>
                  </template>
                </span>
              </template>
              <template v-else-if="!thanks.stripecampsitepayment"
                ><span class="balance-details">
                  <translate>Due:</translate>
                  <translate tag="strong"> On arrival </translate>
                </span>
              </template>
            </td>
            <td>
              <pu-price
                :price="getMoney(thanks.remainder)"
                :display-original-price="true"
              />
            </td>
          </tr>

          <tr v-if="!thanks.stripecampsitepayment" class="balance-highlight">
            <td colspan="2" class="left">
              <i18n-t
                keypath="The remainder is payable direct to {campsite} on arrival: {price}"
              >
                <template #campsite>
                  {{ campsite.name }}
                </template>
                <template #price>
                  <pu-price
                    :price="getMoney(thanks.remainder)"
                    :display-original-price="true"
                  />
                </template>
              </i18n-t>
            </td>
          </tr>
          <tr
            v-else-if="thanks.stripecampsitepayment.status == 'PENDING'"
            class="balance-highlight"
          >
            <td colspan="2" class="left">
              <i18n-t
                keypath="Your card will be debited on the {balanceDueDate}: {price}"
              >
                <template #balanceDueDate>
                  <strong>
                    <pu-date :date="thanks.balanceDueDate" options="full" />
                  </strong>
                </template>
                <template #price>
                  <pu-price
                    :price="getMoney(thanks.remainder)"
                    :display-original-price="true"
                  />
                </template>
              </i18n-t>
            </td>
          </tr>

          <tr
            v-if="
              thanks.stripecampsitepayment &&
              thanks.stripecampsitepayment.status == 'PAID_IMMEDIATE'
            "
          >
            <td colspan="2" class="left">
              <i18n-t
                keypath="{campsite} has billed you the total amount of {price} now."
              >
                <template #campsite>
                  {{ campsite.name }}
                </template>
                <template #price>
                  <pu-price
                    :price="getMoney(thanks.price)"
                    :display-original-price="true"
                  />
                </template>
              </i18n-t>
              <br />
              <translate :tt="{ commission: thanks.commission }">
                This includes a {commission, puNumber, style:percent}
                non-refundable deposit.
              </translate>
            </td>
          </tr>

          <tr v-if="paymentLinkPaidDetails">
            <td colspan="2" class="left">
              <i18n-t
                keypath="{amountPaid} was paid on {datePaid} using a payment link."
              >
                <template #datePaid>
                  <strong
                    ><pu-date
                      :date="paymentLinkPaidDetails.datetime"
                      options="full"
                  /></strong>
                </template>
                <template #amountPaid>
                  <strong
                    ><pu-price
                      :price="getMoney(paymentLinkPaidDetails.amount)"
                      :display-original-price="true"
                  /></strong>
                </template>
              </i18n-t>
            </td>
          </tr>
        </tbody>
      </table>

      <div
        v-if="showCardDetails || thanks.stripePaymentLink"
        class="row"
        :class="{
          'balance-highlight':
            !isBookingPaid && !isPastBooking && !billingError,
          'full-width': !thanks.stripePaymentLink,
        }"
      >
        <span class="card-details">
          <payment-card
            v-if="showCardDetails"
            :card="cardDetails"
            class="current-payment-card"
          />
        </span>

        <i18n-t
          v-if="thanks.stripePaymentLink"
          keypath="You can pay the balance early or using a different card via this {paymentLink}"
          tag="p"
          class="payment-link"
        >
          <template #paymentLink>
            <translate
              tag="a"
              :href="thanks.stripePaymentLink"
              target="_blank"
              class="button account-action"
            >
              payment link
            </translate>
          </template>
        </i18n-t>
      </div>

      <i18n-t
        v-if="billingError"
        tag="p"
        class="billing-highlight error"
        keypath="There was an error billing your card. Please contact {campsiteName} directly to pay the remainder: {price}"
      >
        <template #campsiteName>
          {{ campsite.name }}
        </template>
        <template #price>
          <span class="balance-amount">
            <pu-price
              :price="getMoney(thanks.remainder)"
              :display-original-price="true"
            />
          </span>
        </template>
      </i18n-t>

      <div
        v-if="showBalancePaymentTerms"
        v-thtml="thanks.campsite_BalancePaymentTermsHtml"
        class="rendered-html"
      />

      <div
        v-if="showCampsitePaymentTerms"
        v-thtml="thanks.campsite_PaymentTermsHtml"
        class="rendered-html"
      />

      <campsite-payment-methods
        v-if="campsite.cardTypes"
        :methods="campsite.cardTypes"
        class="payment-methods payment-footnotes"
      />

      <translate
        v-if="campsite.currency != thanks.currencyPreference"
        tag="p"
        class="payment-footnotes currency-footnote"
      >
        Amounts in brackets are indicative based on today's exchange rate - the
        amount paid will depend on your payment provider's exchange rate.
      </translate>
    </section>

    <section class="booking-site-information" id="site-information">
      <h3 class="section-heading">
        <img src="@/assets/images/location-map.svg" width="20" height="19" />
        <translate>Site information</translate>
      </h3>
      <div>
        <campsite-information
          :campsite="campsite"
          :name="campsite.name"
          :slug="campsite.slug"
          :directions="campsite.location.directions"
          :access-code="thanks.pitchType?.campsite.accessCode"
          :email="campsite.email"
          :full-address="campsite.fullAddress2"
          :path="campsite.path"
          :point="campsite.point"
          :tel="campsite.tel"
          :secondary-phone="campsite.secondaryPhone"
          :user-address="userAddress"
          :map-campsite="mapCampsite"
        />
      </div>
    </section>

    <booking-pitch-details
      :pitch-type="thanks.pitchType"
      :door-code="thanks.doorCode"
      :booking="mappedBookingPitchTypeDetails"
      :restrictions="thanks.pitchType?.restrictions"
      :bring-bed-linen-html="thanks.pitchType?.bringBedLinenHtml"
    />

    <ui-collapse :collapse-id="`collapse-your-details`">
      <template #ui-collapse-title>
        <img
          src="@/assets/images/account-icon-blue.svg"
          width="18"
          height="20"
        />
        <translate>Your details</translate>
      </template>
      <template #ui-collapse-body>
        <div>
          <user-details :user="thanks" class="user-details" />
        </div>
      </template>
    </ui-collapse>

    <ui-collapse :collapse-id="`collapse-policies`">
      <template #ui-collapse-title>
        <img
          src="@/assets/images/document.svg"
          width="15"
          height="18"
          class="document-icon"
        />
        <translate> Policies </translate>
      </template>
      <template #ui-collapse-body>
        <div>
          <section>
            <div
              v-if="showPrerenderedSection(thanks.campsite_PaymentTermsHtml)"
              v-thtml="thanks.campsite_PaymentTermsHtml"
            />

            <div
              v-if="showPrerenderedSection(thanks.campsite_TaxTermsHtml)"
              v-thtml="thanks.campsite_TaxTermsHtml"
              class="tax-terms"
            />

            <i18n-t
              keypath="unable to take holiday"
              tag="p"
              class="cancelling-booking"
            >
              <template #cancellingYourBooking>
                <nuxt-link
                  :to="bookingsDetailsUrlParts"
                  class="login-to-cancel-link"
                >
                  <translate>
                    cancelling your booking (requires an account)
                  </translate>
                </nuxt-link>
              </template>
            </i18n-t>

            <thanks-page-contact-the-site
              :telephone="campsite.tel"
              :second-telephone="campsite.secondaryPhone"
              :email="campsite.email"
            />

            <translate tag="p">
              Please note that your deposit is paid to Pitchup.com and is
              non-refundable.
            </translate>
          </section>
        </div>
      </template>
    </ui-collapse>

    <ui-collapse :collapse-id="`collapse-cancellation-policy`">
      <template #ui-collapse-title>
        <img
          src="@/assets/images/document.svg"
          width="15"
          height="18"
          class="document-icon"
        />
        <translate :tt="{ campsiteName: campsite.name }">
          {campsiteName} cancellation or changes
        </translate>
      </template>
      <template #ui-collapse-body>
        <div>
          <section>
            <div
              ref="campsiteCancellationPolicy"
              v-thtml="thanks.campsite_CampsiteTermsAndConditionsHtml"
              class="prerendered"
            />
            <lazy-google-translate-button
              v-if="thanks.campsiteCancellationPolicyNeedsTranslation"
              :parent="campsiteCancellationPolicyRef"
            />
            <translate tag="p" class="cancellation-footnote">
              Please note that your deposit is paid to Pitchup.com and is
              non-refundable.
            </translate>
          </section>
        </div>
      </template>
    </ui-collapse>

    <ui-collapse :collapse-id="`collapse-terms-and-conditions`">
      <template #ui-collapse-title>
        <img
          src="@/assets/images/document.svg"
          width="15"
          height="18"
          class="document-icon"
        />
        <translate> Terms and conditions </translate>
      </template>
      <template #ui-collapse-body>
        <div>
          <section>
            <i18n-t tag="p" keypath="read park terms and conditions">
              <template #termsAndConditions>
                <nuxt-link
                  :to="$puLocalePath({ path: '/terms/' })"
                  target="_blank"
                  rel="noopener"
                >
                  <translate>Pitchup.com terms and conditions</translate>
                </nuxt-link>
              </template>
            </i18n-t>
            <div
              ref="campsiteTermsAndConditions"
              v-thtml="campsite.termsAndConditions"
              class="body-text"
            />
            <lazy-google-translate-button
              v-if="thanks.campsiteTermsAndConditionsNeedsTranslation"
              :parent="campsiteTermsAndConditionsRef"
            />
          </section>
        </div>
      </template>
    </ui-collapse>

    <section class="booking-actions">
      <pu-link v-bind="bookAgainLinkProps" class="account-action">
        <img src="@/assets/images/refresh.svg" class="icon" />
        <translate>Make another booking like this</translate>
      </pu-link>
      <a class="account-action" @click="printPage">
        <img src="@/assets/images/printer.svg" class="icon" />
        <translate>Print booking details</translate>
      </a>
    </section>
    <client-only>
      <div class="vue-portal-target portal-target">
        <portal-target name="popover-target" />
      </div>
    </client-only>
    <nuxt-to-meta :nuxt2meta="head" />
  </div>
</template>

<script lang="ts">
import { csrfTokenService } from '~/apps/csrf/CsrfTokenService'
import { MapCampsite } from '~/apps/maps/domain/MapsCampsite'
import VueHooksInfoMixin from '~/mixins/VueHooksInfoMixin'
import { Component, Prop, toNative, Vue } from '~/utility/pu-class-decorator'
import { LoggerPlugin } from '../../../plugins/logger'
import {
  MoneyType,
  SimpleMoney,
  simpleMoneyToMoney,
} from '../../../utility/money'
import { PaymentCard as Card } from '../../payments/types'
import { PuLinkProps } from '../../pu-links/pu-link/PuLinkProps'
import { BookingThanks } from '../schemas'
import { isDatePast } from '~/utility/date/relative'

@Component({
  setup() {
    const { $log } = useNuxtApp()
    return { log: $log }
  },
  mixins: [VueHooksInfoMixin],
})
class ThanksPage extends Vue {
  log: LoggerPlugin

  @Prop()
  thanks: BookingThanks

  get head() {
    return {
      meta: [this.ogImage, this.ogImageWidth, this.ogImageHeight],
    }
  }

  get campsiteCancellationPolicyRef() {
    // refs are not reactive, so need this little trick to make sure it's there when needed
    if (!this.isMounted) {
      return undefined
    }
    return this.$refs.campsiteCancellationPolicy as Element
  }

  get campsiteTermsAndConditionsRef() {
    // refs are not reactive, so need this little trick to make sure it's there when needed
    if (!this.isMounted) {
      return undefined
    }
    return this.$refs.campsiteTermsAndConditions as Element
  }

  get bookingsDetailsUrlParts() {
    return {
      name: 'account-bookings-prettyId',
      params: { prettyId: this.thanks.prettyId },
    }
  }

  get campsite() {
    return this.thanks.pitchType.campsite
  }

  get mapCampsite(): MapCampsite {
    return {
      bookable: true,
      name: this.campsite.name,
      slug: this.campsite.slug,
      point: this.campsite.point,
      path: this.campsite.path,
      rateCount: this.campsite.rateCount,
      rating10: this.campsite.rating10,
      categories: this.thanks.pitchType.categories,
      pitchtypes: [],
    }
  }

  get billingError() {
    return ['ERROR', 'ERROR_SENT'].includes(
      this.thanks.stripecampsitepayment?.status || '',
    )
  }

  get bookingTaxes() {
    return this.thanks.bookingtaxSet.filter((tax) => tax.hidden === false)
  }

  get showTaxesSection() {
    return this.bookingTaxes.length || this.thanks.bookingarrivaltaxSet.length
  }

  get cardDetails(): Card {
    const cardDetails = this.thanks.cardDetails
    return {
      brand: cardDetails.type || '',
      expMonth: cardDetails.expMonth ? cardDetails.expMonth.toString() : '',
      expYear: cardDetails.expYear ? cardDetails.expYear.toString() : '',
      id: '',
      last4: cardDetails.last4 || '',
    }
  }

  get showCardDetails(): boolean {
    return this.thanks.cardDetails !== null && this.thanks.cardDetails.type
  }

  get showBalancePaymentTerms() {
    return (
      this.thanks.stripecampsitepayment &&
      this.thanks?.stripecampsitepayment?.status !== 'PAID'
    )
  }

  get showCampsitePaymentTerms() {
    const allowedStatuses = ['PENDING', 'REMINDER_SENT']
    return this.thanks.stripecampsitepayment?.status
      ? allowedStatuses.includes(this.thanks.stripecampsitepayment.status)
      : false
  }

  get statusDisplay() {
    const payment = this.thanks.stripecampsitepayment
    const paymentStatus = this.thanks.stripecampsitepayment?.status
    const validStatuses = [
      'REMINDER_SENT',
      'EXPIRED_SENT',
      'ERROR',
      'ERROR_SENT',
    ]
    return payment && paymentStatus && validStatuses.includes(paymentStatus)
      ? payment.statusDisplay
      : null
  }

  get mappedBookingPitchTypeDetails() {
    return {
      ...this.thanks,
      campsiteUsePermittedArrivalDepartTimes:
        this.thanks.pitchType.campsite.usePermittedArrivalDepartTimes,
      adults: this.thanks.adults,
      childAges: this.thanks.childAges,
    }
  }

  get ratingObject() {
    return {
      rate: this.campsite.rating10,
      votes: this.campsite.rateCount,
    }
  }

  get isBookingPaid() {
    return (
      this.thanks.stripecampsitepayment?.status === 'PAID' ||
      this.thanks.stripecampsitepayment?.status === 'PAID_IMMEDIATE'
    )
  }

  get isPastBooking() {
    return isDatePast(this.thanks.depart)
  }

  get bookAgainLinkProps(): PuLinkProps {
    return {
      name: 'booking-make_another_booking-pretty_id',
      params: { pretty_id: this.thanks.prettyId },
      excludeType: 'all',
    }
  }

  get userAddress() {
    return [
      this.thanks.address,
      this.thanks.city,
      this.thanks.county,
      this.thanks.postcode,
    ]
      .filter((el) => el !== '')
      .join(', ')
  }

  get paymentLinkPaidDetails() {
    return this.thanks.stripecampsitepayment?.paymentLinkPaidDetails
  }

  get ogImage(): Meta {
    return {
      hid: 'og:image',
      property: 'og:image',
      content: this.thanks?.pitchType.primaryPhoto?.url.masterImage,
    }
  }

  get ogImageWidth(): Meta {
    return {
      hid: 'og:image:width',
      property: 'og:image:width',
      content: '180',
    }
  }

  get ogImageHeight(): Meta {
    return {
      hid: 'og:image:height',
      property: 'og:image:height',
      content: '180',
    }
  }

  async mounted() {
    await this.log('thanks_page_mounted')
  }

  getMoney(price: SimpleMoney): MoneyType {
    return simpleMoneyToMoney(price)
  }

  showPrerenderedSection(text: string) {
    return text.replace(/\s+/g, ' ').trim().length > 0
  }

  printPage() {
    window.print()
  }

  async updateNewsletter(value: boolean) {
    const { csrfToken } = await csrfTokenService()
    await $fetch('/_/users/booking-newsletter/', {
      method: 'PATCH',
      body: {
        email: this.thanks.email,
        booking_signature: this.thanks.signature,
        newsletter_subscribed: value,
      },
      headers: {
        'X-CSRFToken': csrfToken,
      },
    })
  }
}

export default toNative(ThanksPage)
</script>
<style>
@import url('~/assets/css/user-account.css');
@import url('~/assets/css/booking-detail.css');
</style>
<style scoped>
.intro {
  padding: 1.4em;
}

.intro {
  font-size: var(--body);
}

.intro :deep(em),
em {
  font-weight: bold;
}

.thanks-page :deep(.ui-collapse-content) {
  padding: 1.4em;
}

.section-header {
  margin: 1em 0;
  border-bottom: 2px solid var(--colour-dark-blue);
  position: relative;
}

.section-header span {
  color: white;
  padding: 0.5em 1em 0.5em 1.4em;
  display: inline-block;
  border-radius: 0 0.25em 0 0;
  font-size: var(--body);
  font-weight: bold;
  max-width: 85%;
  background-color: var(--colour-dark-blue);
}

.booking-id {
  color: var(--colour-dark-blue);
  font-size: var(--title);
  position: absolute;
  right: 1em;
  top: 0.25em;
  font-weight: 400;
}

.view-site-btn {
  flex-shrink: 0;
}

.cs-details {
  padding: 1.4em;
  border-bottom: 1px solid var(--colour-page-background);
}

.tax-terms :deep(h2) {
  font-size: 1em;
  margin: 1em 0 0 0;
}

.affiliates {
  background-color: #eff6ff;
  padding: 1em;
}

.affiliates p {
  font-size: var(--title);
}

.affiliates-row {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  margin-top: 1em;
}

.affiliate-link {
  user-select: none;
  -webkit-user-select: none;
  border: 1px solid #d6e6f1;
  border-radius: 0.5em;
  background-color: white;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
  padding: 0.5em 1em;
  text-decoration: none;
  color: var(--colour-dark-blue);
  max-width: calc(50vw - 2em);
}

.affiliate-link img {
  height: 2em;
  width: auto;
}

.affiliate-link p {
  margin: 0.5em 0 0 0;
  display: inline-block;
  text-align: center;
  font-size: var(--body);
}

.trustpilot img {
  max-height: 1.5em;
  margin-top: 0.35em;
}

.email-newsletter-toggle {
  margin: 1em 1em 0 1em;
  display: inline-block;
}

.email-newsletter-toggle :deep(label) {
  justify-content: start;
  flex-direction: row;
  line-height: 1.1;
  text-wrap-style: pretty;
}

.cancellation-footnote {
  margin-top: 1em;
}

.cancelling-booking {
  margin: 1em 0;
}

.login-to-cancel-link,
.login-to-cancel-link + div {
  display: inline-flex;
}

.location :deep(.location-details) {
  align-items: flex-start;
  display: flex;
  flex-direction: column-reverse;
  padding: 0 1em 0 0;
}

.location :deep(.address) {
  margin-top: 0.5em;
  margin-bottom: 0.75em;
}

.location :deep(.pu-map) {
  isolation: isolate;
}

/* desktop & tablet */
.thanks-page.desktop {
  margin: auto;
  max-width: var(--legacy-page-width-max);
  width: 100%;
}

.thanks-page.desktop .affiliates {
  border-radius: var(--border-radius);
  padding: 1.4em;
}

.desktop .affiliates-row {
  justify-content: space-between;
  column-gap: 3em;
}

.thanks-page.desktop .affiliate-link {
  max-width: none;
  flex: 1 0 auto;
}

.desktop table,
.desktop p,
.desktop li,
.desktop .prerendered :deep(p),
.desktop .success,
.desktop .section-header span,
.desktop .cancelling-booking,
.thanks-page.desktop :deep(.reservation-details p),
.thanks-page.desktop :deep(.pt-name),
.desktop .reservation-details :deep(.unit-per-pitch),
.thanks-page.desktop :deep(.address),
.thanks-page.desktop :deep(.booking-dates) {
  font-size: revert;
}

.desktop .section-header {
  margin-top: 2em;
  border-width: 4px;
}

.desktop .section-header span {
  border-radius: 0.6em 0.6em 0 0;
}

.desktop .email-newsletter-toggle {
  margin-left: 1.4em;
}

.thanks-page.desktop section.reservation-details {
  padding-top: 1em;
}

.desktop .location :deep(.location-details) {
  padding: 0;
  flex-direction: column;
}

.desktop .location :deep(.map-holder) {
  width: 50%;
  min-height: 16em;
  border-radius: 0.6em;
  overflow: hidden;
}
</style>
